import { useEffect, useRef } from "react";

/* ────────────────────────────────────────────────────────────
   HERO — scroll-scrubbed espresso pour.
   The video is decorative; every word and the CTA work without it.
   ──────────────────────────────────────────────────────────── */

const VIDEO_URL = "/assets/hero/hero-scrub.mp4";
const POSTER_URL = "/assets/hero/hero-poster.jpg";
const ENDING_URL = "/assets/hero/hero-ending.jpg";
const VIDEO_BYTES = 4563672;

/* The five static-hero gates. These strings must stay
   character-for-character identical to the media queries in landing.css. */
const GATES = [
  "(max-width: 720px)",
  "(orientation: portrait) and (max-width: 1024px)",
  "(orientation: portrait) and (pointer: coarse)",
  "(orientation: landscape) and (pointer: coarse) and (max-height: 560px)",
  "(prefers-reduced-motion: reduce)",
];

const BANDS = [
  { a: 0.0, b: 0.21, anim: "drift", sa: 0.7 },
  { a: 0.25, b: 0.47, anim: "blur", sa: 0.66 },
  { a: 0.51, b: 0.73, anim: "snap", sa: 0.66 },
  { a: 0.77, b: 1.0, anim: "settle", sa: 0.68 },
];

const smoothstep = (p, e0, e1) => {
  const t = Math.min(1, Math.max(0, (p - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
};
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

/* seeded generator: the "random" offsets are identical on every load */
function rng(seed) {
  let s = seed >>> 0;
  return () => (s = (s * 1664525 + 1013904223) >>> 0) / 4294967296;
}

/* Split a headline into word/char spans with a hidden copy for screen readers. */
function splitText(el, mode, seed, spread = 0.42) {
  if (!el || el.dataset.split === "1") return;
  const text = el.textContent;
  el.dataset.split = "1";
  el.textContent = "";

  const sr = document.createElement("span");
  sr.className = "sr-only";
  sr.textContent = text;
  el.appendChild(sr);

  const vis = document.createElement("span");
  vis.setAttribute("aria-hidden", "true");

  const words = text.split(" ");
  const rand = rng(seed);
  const totalChars = text.replace(/\s/g, "").length;
  let ci = 0;

  words.forEach((word, wi) => {
    const w = document.createElement("span");
    w.className = "w";
    w.style.setProperty("--th", (wi / Math.max(1, words.length)) * spread + "");

    if (mode === "snap") {
      [...word].forEach((ch) => {
        const c = document.createElement("span");
        c.className = "c";
        c.textContent = ch;
        c.style.setProperty(
          "--th",
          (ci / Math.max(1, totalChars)) * spread + rand() * 0.06 + "",
        );
        c.style.setProperty("--jx", (rand() < 0.5 ? -1 : 1) * 16 + "px");
        w.appendChild(c);
        ci++;
      });
    } else {
      w.textContent = word;
    }

    vis.appendChild(w);
    if (wi < words.length - 1) {
      const sp = document.createElement("span");
      sp.className = "w";
      sp.textContent = " ";
      vis.appendChild(sp);
    }
  });

  el.appendChild(vis);
}

export default function HeroScrub({ onNavigate }) {
  const heroRef = useRef(null);
  const stageRef = useRef(null);
  const videoRef = useRef(null);
  const posterRef = useRef(null);
  const ringRef = useRef(null);
  const bandRefs = useRef([]);

  useEffect(() => {
    const hero = heroRef.current;
    const stage = stageRef.current;
    const video = videoRef.current;
    const poster = posterRef.current;
    const ring = ringRef.current;
    const bands = bandRefs.current.filter(Boolean);
    if (!hero || !stage || !video) return;

    /* ── split the headlines once ─────────────────────────── */
    bands.forEach((el, i) => {
      const h = el.querySelector("[data-split-target]");
      splitText(h, BANDS[i].anim, 1337 + i * 977);
      el.style.setProperty("--sa", BANDS[i].sa + "");
    });

    /* per-band caches, so the DOM is touched only on change */
    const cache = bands.map(() => ({ op: -1, k: -1 }));

    let target = 0;
    let shown = 0;
    let rafId = null;
    let lastTick = 0;
    let seekBusy = false;
    let pendingTime = null;
    let heroOnScreen = true;
    let scrubOn = false;
    let started = false;
    let loadStart = 0;

    const heroProgress = () => {
      const rect = hero.getBoundingClientRect();
      const range = hero.offsetHeight - window.innerHeight;
      if (range <= 0) return 0;
      return clamp(-rect.top / range, 0, 1);
    };

    /* ── seek gating: never overlap, never deadlock ───────── */
    function requestSeek(t) {
      if (!video.duration || Number.isNaN(video.duration)) return;
      if (seekBusy) {
        pendingTime = t;
        return;
      }
      seekBusy = true;
      video.currentTime = t;
    }
    const onSeeked = () => {
      seekBusy = false;
      if (pendingTime !== null) {
        const t = pendingTime;
        pendingTime = null;
        requestSeek(t);
      }
    };
    const onVideoError = () => {
      seekBusy = false;
      pendingTime = null;
      failVideo();
    };
    video.addEventListener("seeked", onSeeked);
    video.addEventListener("error", onVideoError);

    /* ── captions ─────────────────────────────────────────── */
    function updateCaptions(p) {
      const loadK = loadStart
        ? clamp((performance.now() - loadStart) / 900, 0, 1)
        : 0;

      bands.forEach((el, i) => {
        const { a, b } = BANDS[i];
        const f = Math.min(0.02, (b - a) / 3);
        const inFade = i === 0 ? 1 : smoothstep(p, a, a + f);
        const outFade =
          i === bands.length - 1 ? 0 : smoothstep(p, b - f, b);
        const op = inFade * (1 - outFade);

        const ramp =
          parseFloat(el.dataset.ramp) || Math.min(0.025, (b - a) * 0.35);
        let k = clamp((p - a) / ramp, 0, 1);
        if (i === 0) k = Math.max(k, loadK);

        const c = cache[i];
        if (Math.abs(op - c.op) > 0.004) {
          c.op = op;
          el.style.opacity = op.toFixed(3);
          el.style.visibility = op < 0.01 ? "hidden" : "visible";
        }
        if (Math.abs(k - c.k) > 0.008) {
          c.k = k;
          el.style.setProperty("--k", k.toFixed(3));
        }
      });

      if (p > 0.06) hero.classList.add("cue-hidden");
      else hero.classList.remove("cue-hidden");
    }

    /* ── the rAF loop that rests ──────────────────────────── */
    function tick(now) {
      const dt = Math.min(100, now - (lastTick || now));
      lastTick = now;
      const kk = 0.16;
      shown += (target - shown) * (1 - Math.pow(1 - kk, dt / 16.667));

      const loading = loadStart && performance.now() - loadStart < 950;
      if (Math.abs(target - shown) < 0.0005 && !loading) {
        shown = target;
        rafId = null;
        lastTick = 0;
      } else {
        rafId = requestAnimationFrame(tick);
      }
      if (video.duration) requestSeek(shown * video.duration);
      updateCaptions(shown);
    }

    function onScroll() {
      target = heroProgress();
      if (rafId === null && heroOnScreen) rafId = requestAnimationFrame(tick);
    }

    /* ── blob load, behind an honest ring ─────────────────── */
    function failVideo() {
      if (ring) ring.style.display = "none";
      stage.classList.add("video-failed");
    }

    async function loadHeroBlob() {
      const ctrl = new AbortController();
      let watchdog = setTimeout(() => ctrl.abort(), 20000);
      const res = await fetch(VIDEO_URL, {
        priority: "low",
        signal: ctrl.signal,
      });
      if (!res.ok || !res.body) throw new Error("hero video unavailable");
      const total = Number(res.headers.get("Content-Length")) || VIDEO_BYTES;
      const reader = res.body.getReader();
      const chunks = [];
      let got = 0;
      let lastRing = 0;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        clearTimeout(watchdog);
        watchdog = setTimeout(() => ctrl.abort(), 20000);
        chunks.push(value);
        got += value.length;
        const frac = Math.min(1, got / total);
        const now = performance.now();
        if (now - lastRing > 100 || frac === 1) {
          lastRing = now;
          if (ring)
            ring.style.setProperty("--ld", Math.round(126 * (1 - frac)) + "");
        }
      }
      clearTimeout(watchdog);
      if (ring) ring.style.setProperty("--ld", "0");
      video.src = URL.createObjectURL(new Blob(chunks));
      video.load();
      video.addEventListener(
        "canplay",
        () => {
          requestSeek(heroProgress() * video.duration);
          stage.classList.add("video-ready");
        },
        { once: true },
      );
    }

    function initHeroOnce() {
      if (started) return;
      started = true;
      loadStart = performance.now();
      if (poster) poster.style.backgroundImage = `url('${POSTER_URL}')`;

      let kicked = false;
      const kick = () => {
        if (kicked) return;
        kicked = true;
        loadHeroBlob().catch(failVideo);
      };
      const img = new Image();
      img.onload = kick;
      img.onerror = kick;
      img.src = POSTER_URL;
      setTimeout(kick, 4000);
    }

    /* ── the gate, decided live ───────────────────────────── */
    function enableScrub() {
      if (scrubOn) return;
      scrubOn = true;
      initHeroOnce();
      window.addEventListener("scroll", onScroll, { passive: true });
      cache.forEach((c) => {
        c.op = -1;
        c.k = -1;
      });
      updateCaptions(heroProgress());
      onScroll();
    }
    function disableScrub() {
      if (!scrubOn) return;
      scrubOn = false;
      window.removeEventListener("scroll", onScroll);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    }
    const MQLS = GATES.map((q) => window.matchMedia(q));
    const applyHeroMode = () => {
      if (MQLS.some((m) => m.matches)) disableScrub();
      else enableScrub();
    };
    MQLS.forEach((m) => m.addEventListener("change", applyHeroMode));

    const io = new IntersectionObserver(
      ([e]) => {
        heroOnScreen = e.isIntersecting;
        if (heroOnScreen && scrubOn && rafId === null) onScroll();
      },
      { threshold: 0 },
    );
    io.observe(hero);

    applyHeroMode();

    return () => {
      disableScrub();
      MQLS.forEach((m) => m.removeEventListener("change", applyHeroMode));
      io.disconnect();
      video.removeEventListener("seeked", onSeeked);
      video.removeEventListener("error", onVideoError);
    };
  }, []);

  const go = (id) => {
    if (onNavigate) return onNavigate(id);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const ctas = (
    <div className="band-cta">
      <button className="btn btn-gold" onClick={() => go("kontakt")}>
        Få smagsprøver
      </button>
      <button className="btn btn-ghost" onClick={() => go("sortiment")}>
        Se blandingerne
      </button>
    </div>
  );

  return (
    <>
      {/* ── scrub hero (laptops and desktops) ── */}
      <section className="hero" ref={heroRef} aria-label="Amigos Caffè">
        <div className="hero-stage" ref={stageRef}>
          <div className="hero-poster" ref={posterRef} aria-hidden="true" />
          <video
            className="hero-video"
            ref={videoRef}
            preload="none"
            muted
            playsInline
            aria-hidden="true"
            tabIndex={-1}
          />
          <div className="hero-scrim" aria-hidden="true" />
          <div className="hero-grain" aria-hidden="true" />

          <svg className="hero-ring" ref={ringRef} viewBox="0 0 48 48" aria-hidden="true">
            <circle
              cx="24"
              cy="24"
              r="20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeDasharray="126"
              style={{ strokeDashoffset: "var(--ld, 126)" }}
            />
          </svg>

          {/* band 1 */}
          <div
            className="band"
            data-anim="drift"
            ref={(el) => (bandRefs.current[0] = el)}
          >
            <p className="band-kicker">Amigos Caffè · Trieste · 1980</p>
            <h1 className="band-h" data-split-target>
              Ægte italiensk espresso
            </h1>
            <p className="band-sub">Ristet i Trieste. Skænket i Danmark.</p>
          </div>

          {/* band 2 */}
          <div
            className="band"
            data-anim="blur"
            ref={(el) => (bandRefs.current[1] = el)}
          >
            <div className="bl-wrap">
              <h2 className="band-h bl-soft" aria-hidden="true">
                Cremaen er beviset.
              </h2>
              <h2 className="band-h bl-sharp" data-split-target>
                Cremaen er beviset.
              </h2>
            </div>
            <p className="band-sub">
              Gylden, tæt og vedvarende. Den fortæller alt om bønnen under.
            </p>
          </div>

          {/* band 3 */}
          <div
            className="band"
            data-anim="snap"
            ref={(el) => (bandRefs.current[2] = el)}
          >
            <h2 className="band-h" data-split-target>
              Samme kop. Hver gang.
            </h2>
            <p className="band-sub">
              Fra risteriet i Trieste til din maskine, uden at bryde kæden.
            </p>
          </div>

          {/* band 4 — the settle */}
          <div
            className="band"
            data-anim="settle"
            ref={(el) => (bandRefs.current[3] = el)}
          >
            <h2 className="band-h" data-split-target>
              Klar til din bar.
            </h2>
            <p className="band-sub">
              Officiel dansk distributør af Amigos Caffè. Til caféer,
              restauranter, hoteller og kontorer.
            </p>
            {ctas}
          </div>

          <div className="hero-cue" aria-hidden="true">
            <span>Scroll</span>
            <span className="hero-cue-line" />
          </div>
        </div>
      </section>

      {/* ── static hero (phones, portrait tablets, reduced motion) ── */}
      <section className="hero-static" aria-label="Amigos Caffè">
        <div
          className="hero-static-media"
          style={{ backgroundImage: `url('${ENDING_URL}')` }}
          aria-hidden="true"
        />
        <div className="hero-scrim" aria-hidden="true" />
        <div className="hero-grain" aria-hidden="true" />
        <div className="hero-static-inner">
          <p className="band-kicker">Amigos Caffè · Trieste · 1980</p>
          <h1 className="band-h">Ægte italiensk espresso</h1>
          <p className="band-sub">
            Ristet i Trieste siden 1980. Leveret i Danmark til caféer,
            restauranter, hoteller og kontorer.
          </p>
          {ctas}
        </div>
      </section>
    </>
  );
}
